
import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);

import classNames from 'classnames';
import { Button } from '@usitsdasdesign/dds-react';
import Text from 'atoms/Text';
import ShiftSelect from 'molecules/ShiftSelect';
import Image from 'next/image';
import useGlobalToastStore from 'store/globalToastStore';

function TimeSheetCalendar({
  startDate,
  endDate,
  shiftData,
  setShiftData,
  shiftMasterData,
  readOnly = false,
  showMissingShiftError = false,
  isCallSupport = false,
  handleCallSupport = () => { },
  isOCSView = false,
  isDemandShift = false
}) {
  const [currentMonth, setCurrentMonth] = useState(dayjs(startDate));
  useEffect(() => {
    if (startDate && dayjs(startDate).isValid()) {
      setCurrentMonth(dayjs(startDate));
    }
  }, [startDate])
  const monthStart = useMemo(() => currentMonth.startOf('month'), [currentMonth]);
  const monthEnd = useMemo(() => currentMonth.endOf('month'), [currentMonth]);
  const calendarStart = useMemo(() => monthStart.startOf('week'), [monthStart]);
  const calendarEnd = useMemo(() => monthEnd.endOf('week'), [monthEnd]);
  const { setToastData } = useGlobalToastStore();

  const generateCalendar = () => {
    let date = calendarStart.clone();
    const calendar = [];
    while (date.isBefore(calendarEnd)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push({
          day: date.clone(),
          shift: (shiftData ?? {})[date.format('YYYY-MM-DD')] || null
        })
        date = date.add(1, 'day');
      }
      calendar.push(week);
    }
    return calendar;
  };

  const handleShiftChange = (dateStr, e) => {
    const shiftForTheDate = shiftData?.[dateStr] ?? {};
    if (shiftForTheDate?.onCallSupport?.onCallSupportShiftTime?.id === e.id) {
      setToastData({ message: `This shift overlaps with an existing ${isDemandShift ? 'demand' : 'allocation'} shift. Please choose a different time!`, type: 'error', show: true });
      return;
    }
    const updatedData = Object.assign({}, shiftData, { [dateStr]: Object.assign({}, shiftForTheDate, e, { showShiftOnCallConflict: false }) });
    setShiftData(updatedData);
  };

  const calendar = useMemo(() => generateCalendar(), [calendarStart, calendarEnd, shiftData]);
  return (
    <div>
      <div className='flex justify-between flex-wrap gap-3 items-center w-full max-w-[922px] mb-4 lg:mb-0'>
        <div className="flex justify-between items-center mb-0.5 max-w-[256px]">
          <Button
            isDisabled={currentMonth.subtract('1', 'month').isBefore(dayjs(startDate), 'month')}
            size='l' className='justify-center' kind="primaryLoud" icon='chevron_left__l__stroke' onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))} />
          <Text className="border border-gray-200 p-2 w-[152px] text-center" color='text-black'>{currentMonth.format('MMMM YYYY')}</Text>
          <Button
            isDisabled={currentMonth.add('1', 'month').isAfter(dayjs(endDate), 'month')}
            size='l' className='justify-center' kind="primaryLoud" icon='chevron_right__l__stroke' onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))} />
        </div>
        {isCallSupport && <div className="flex gap-4 ">
          <div className="flex items-center gap-2">
            <Image
              src='/images/icons/call-stroke.svg'
              width={16}
              height={16}
            />
            <span className='text-xs text-black'>Add on-call support</span>
          </div>
          <div className="flex items-center gap-2">
            <Image
              src='/images/icons/call.svg'
              width={16}
              height={16}
            />
            <span className='text-xs text-black'>View/Edit Call Support</span>
          </div>
        </div>}
      </div>
      <div className='overflow-x-auto'>
        <div className='w-[922px] lg:w-full max-w-[922px]'>
          <div className="grid grid-cols-7 gap-0.5">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div className='bg-gray-500 text-left p-2' key={day}><Text variant='labelSemibold'>{day}</Text></div>
            ))}
          </div>

          {calendar.map((week, i) => (
            <div key={i} className="grid grid-cols-7 gap-0.5 mb-1">
              {week.map(({ day, shift }) => {
                const dateStr = day.format('YYYY-MM-DD');

                const isOutsideRange =
                  (startDate && day.isBefore(dayjs(startDate), 'day')) ||
                  (endDate && day.isAfter(dayjs(endDate), 'day'));
                const isOutsideCurrentMonth = day.month() !== currentMonth.month();
                const isWeekend = [0, 6].includes(day.day());
                const isOnCallSupport = shift?.onCallSupport?.isOnCallSupport;
                const shiftValue = isCallSupport ? shift?.onCallSupport?.onCallSupportShiftTime ?? null : (shift?.id ? shift ?? null : null);
                const isOnCallConflict = !isOutsideRange && !isOutsideCurrentMonth && isCallSupport && shift?.showOnCallConflict;
                const isShiftOnCallConflict = !isOutsideRange && !isOutsideCurrentMonth && !isCallSupport && shift?.showShiftOnCallConflict;
                return (
                  <div
                    key={dateStr}
                    className={classNames(
                      'h-[80px] py-4  border border-gray-200 text-sm relative flex flex-col justify-between',
                      isOutsideCurrentMonth && 'bg-disable-pattern',
                      isOutsideRange && 'bg-disable-pattern opacity-50 pointer-events-none',
                      (!isOutsideRange && !isOutsideCurrentMonth && showMissingShiftError && !isWeekend && !shift) && 'bg-red-100 border-red-500',
                      (isOnCallConflict) && 'bg-red-100 border-red-500',
                      (isShiftOnCallConflict) && 'bg-red-100 border-red-500',
                      (!isOutsideRange && isCallSupport && !isOCSView && 'cursor-pointer')
                    )}
                    onClick={() => {
                      if (!isOutsideRange && isCallSupport && !isOutsideCurrentMonth && !isOCSView) {
                        handleCallSupport(dateStr)
                      }
                    }}
                  >
                    <div className='flex items-center justify-between gap-4 px-2 mb-2'>
                      <span className="text-sm text-black font-semibold text-center">{day.date()}</span>
                      {!isOutsideRange && !isOutsideCurrentMonth && isCallSupport && (
                        <Image
                          src={isOnCallSupport ? '/images/icons/call.svg' : '/images/icons/call-stroke.svg'}
                          width={16}
                          height={16}
                          className="cursor-pointer"
                          onClick={(e) => handleCallSupport(dateStr, e)}
                        />
                      )}
                    </div>
                    {!isOutsideRange && (
                      <ShiftSelect
                        placeholder={isCallSupport ? "Select OCS Shift" : (isOCSView && readOnly) ? 'No shift' : 'Select Shift'}                        
                        isDisabled={readOnly}
                        readOnly={readOnly}
                        key={dateStr}
                        className="mt-auto"
                        value={shiftValue}
                        shifts={shiftMasterData}
                        size="s"
                        height={24}
                        onChange={(e) => handleShiftChange(dateStr, e)}
                      />
                    )}

                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

export default TimeSheetCalendar;